import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import { saveAs } from "file-saver";
import "./PlateBlurTool.css";

type Job = {
  job_id: string;
  status: "queued" | "processing" | "done" | "error";
  processed_frames?: number;
  total_frames?: number;
  message?: string | null;
};
const toolBase = (
  process.env.REACT_APP_PLATE_API_URL ||
  (process.env.NODE_ENV === "development"
    ? "/plate-api"
    : "https://tools.nuhman.com/api")
).replace(/\/+$/, "");
const fileSize = (size: number) =>
  size < 1024 * 1024
    ? `${Math.ceil(size / 1024)} KB`
    : `${(size / (1024 * 1024)).toFixed(1)} MB`;
export default function PlateBlurTool() {
  const [file, setFile] = useState<File | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [error, setError] = useState("");
  const [pollError, setPollError] = useState(false);
  const [pollAttempt, setPollAttempt] = useState(0);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const request = useRef<AbortController | null>(null);
  const active =
    uploading ||
    downloading ||
    job?.status === "queued" ||
    job?.status === "processing";
  const jobId = job?.job_id;
  const terminal = job?.status === "done" || job?.status === "error";
  useEffect(() => () => request.current?.abort(), []);
  useEffect(() => {
    if (!jobId || terminal) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    async function poll() {
      try {
        const { data } = await axios.get<Job>(
          `${toolBase}/status/${encodeURIComponent(jobId!)}`,
          { signal: controller.signal, timeout: 15000 },
        );
        if (!["queued", "processing", "done", "error"].includes(data.status))
          throw new Error("Unexpected status");
        setJob({ ...data, job_id: jobId! });
        setPollError(false);
        if (data.status === "error")
          setError(
            data.message || "Processing failed. Please try another video.",
          );
        if (data.status !== "done" && data.status !== "error")
          timer = setTimeout(poll, 2000);
      } catch (err) {
        if (!axios.isCancel(err)) setPollError(true);
      }
    }
    timer = setTimeout(poll, 1000);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [jobId, terminal, pollAttempt]);
  function selectFile(next: File | undefined) {
    if (active || !next) return;
    if (
      !next.type.startsWith("video/") &&
      !/\.(mp4|mov|mkv|webm|avi)$/i.test(next.name)
    ) {
      setError("Please choose a video file, such as MP4, MOV, or MKV.");
      return;
    }
    if (next.size === 0) {
      setError("This file is empty. Please choose another video.");
      return;
    }
    setFile(next);
    setJob(null);
    setError("");
    setPollError(false);
    setDownloaded(false);
    setUploadPercent(null);
  }
  async function upload() {
    if (!file || active) return;
    const controller = new AbortController();
    request.current = controller;
    setUploading(true);
    setError("");
    setJob(null);
    setDownloaded(false);
    setUploadPercent(null);
    setPollError(false);
    const data = new FormData();
    data.append("file", file);
    try {
      const res = await axios.post<{ job_id: string }>(
        `${toolBase}/upload`,
        data,
        {
          signal: controller.signal,
          onUploadProgress: (event) =>
            setUploadPercent(
              event.total
                ? Math.round((event.loaded / event.total) * 100)
                : null,
            ),
        },
      );
      if (!res.data.job_id)
        throw new Error(
          "The upload did not return a processing job. Please try again.",
        );
      setJob({ job_id: res.data.job_id, status: "queued" });
    } catch (err) {
      if (!axios.isCancel(err))
        setError(
          "Your video couldn’t be uploaded. Please check your connection and try again.",
        );
    } finally {
      if (!controller.signal.aborted) setUploading(false);
    }
  }
  async function download() {
    if (!jobId || downloading) return;
    const controller = new AbortController();
    request.current = controller;
    setDownloading(true);
    setError("");
    try {
      const res = await axios.get(
        `${toolBase}/download/${encodeURIComponent(jobId)}`,
        { responseType: "blob", signal: controller.signal },
      );
      saveAs(
        res.data,
        `${file?.name.replace(/\.[^.]+$/, "") || jobId}_blurred.mp4`,
      );
      setDownloaded(true);
    } catch (err) {
      if (!axios.isCancel(err))
        setError(
          "The download failed or has expired. Try again, or process your video again.",
        );
    } finally {
      if (!controller.signal.aborted) setDownloading(false);
    }
  }
  function reset() {
    if (active) return;
    setFile(null);
    setJob(null);
    setError("");
    setDownloaded(false);
    setPollError(false);
    if (input.current) input.current.value = "";
  }
  const percent = job?.total_frames
    ? Math.min(
        100,
        Math.max(
          0,
          Math.round(((job.processed_frames || 0) / job.total_frames) * 100),
        ),
      )
    : null;
  const done = job?.status === "done";
  return (
    <div className="ed-wrap ed-page plate-page">
      <Helmet>
        <title>Number Plate Blurring — Nuhman Tools</title>
        <meta
          name="description"
          content="Automatically detect and blur license plates in your video, then download the processed result."
        />
      </Helmet>
      <nav className="plate-breadcrumb" aria-label="Breadcrumb">
        <Link to="/smart-tools">Tools & utilities</Link>
        <span>/</span>
        <span>Number Plate Blurring</span>
      </nav>
      <header className="plate-heading">
        <p className="ed-eyebrow">
          <span className="ed-dot" />
          The toolkit · Video privacy
        </p>
        <h1>
          Number Plate Blurring<span>.</span>
        </h1>
        <p>
          Share the moment. Keep plates private.
          <br />
          Automatically detect and blur license plates in your video.
        </p>
      </header>
      <div className="plate-layout">
        <section className="plate-workspace" aria-labelledby="upload-title">
          <div className="plate-panel-heading">
            <span className="ed-eyebrow">01 / Your video</span>
            <span>MP4 · MOV · MKV</span>
          </div>
          <h2 id="upload-title">
            A little more privacy.
            <br />
            Just one upload.
          </h2>
          <p className="plate-helper">
            Choose your video. We’ll take care of the number plates.
          </p>
          <div
            className={`plate-dropzone ${dragging ? "is-dragging" : ""} ${file ? "has-file" : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              if (!active) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              selectFile(event.dataTransfer.files[0]);
            }}
          >
            <span className="plate-upload-icon" aria-hidden="true">
              {file ? "▧" : "↑"}
            </span>
            {file ? (
              <>
                <h3 className="plate-file-name">{file.name}</h3>
                <p>{fileSize(file.size)} · Selected video</p>
              </>
            ) : (
              <>
                <h3>Drop your video here</h3>
                <p>or choose a file from your device</p>
              </>
            )}
            <input
              ref={input}
              id="plate-file"
              className="plate-file-input"
              type="file"
              accept="video/*,.mkv"
              disabled={!!active}
              onChange={(event) => selectFile(event.target.files?.[0])}
              aria-label="Choose video"
            />
            <button
              className="plate-secondary"
              onClick={() => input.current?.click()}
              disabled={!!active}
            >
              {file ? "Change video" : "Choose video"}{" "}
              <span aria-hidden="true">↗</span>
            </button>
          </div>
          <div className="plate-upload-actions">
            <p>Processing starts when you’re ready.</p>
            <button
              className="ed-button"
              onClick={upload}
              disabled={!file || !!active || done}
            >
              {uploading
                ? `Uploading${uploadPercent !== null ? ` ${uploadPercent}%` : "…"}`
                : "Blur number plates ↗"}
            </button>
          </div>
          {error && (
            <p className="plate-error" role="alert">
              {error}
            </p>
          )}
          {(uploading || job) && (
            <section className="plate-job" aria-label="Video processing">
              <div className="plate-job-heading">
                <h3>
                  {downloaded
                    ? "Your download is ready."
                    : done
                      ? "Your video is ready."
                      : uploading
                        ? "Uploading your video…"
                        : job?.status === "error"
                          ? "We couldn’t process this video."
                          : job?.status === "processing"
                            ? "Blurring number plates…"
                            : "Your video is in the queue."}
                </h3>
                <span>
                  {done
                    ? "✓"
                    : job?.status === "processing" && percent !== null
                      ? `${percent}%`
                      : "◷"}
                </span>
              </div>
              <p role="status">
                {downloaded
                  ? "Check your downloads for the processed video."
                  : done
                    ? "Download your result and review it before sharing."
                    : uploading
                      ? "Keep this page open while your video uploads."
                      : job?.status === "processing"
                        ? "We’re checking your video frame by frame."
                        : job?.status === "queued"
                          ? "Processing will start as soon as a worker is available."
                          : "You can choose another video or try again."}
              </p>
              {!done && job?.status !== "error" && (
                <div
                  className="plate-progress"
                  role="progressbar"
                  aria-label={
                    uploading ? "Upload progress" : "Processing progress"
                  }
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={
                    (uploading ? uploadPercent : percent) ?? undefined
                  }
                >
                  <span
                    style={{
                      width: `${(uploading ? uploadPercent : percent) ?? 8}%`,
                    }}
                  />
                </div>
              )}
              {job?.status === "processing" && !!job.total_frames && (
                <small>
                  {job.processed_frames || 0} / {job.total_frames} frames
                  processed
                </small>
              )}
              {pollError && (
                <div className="plate-poll-error" role="alert">
                  <p>
                    We lost the status connection. Your video may still be
                    processing.
                  </p>
                  <button
                    className="plate-secondary"
                    onClick={() => {
                      setPollError(false);
                      setPollAttempt((n) => n + 1);
                    }}
                  >
                    Check status again ↻
                  </button>
                </div>
              )}
              {done && (
                <div className="plate-result-actions">
                  <button
                    className="ed-button"
                    onClick={download}
                    disabled={downloading || downloaded}
                  >
                    {downloading
                      ? "Preparing download…"
                      : downloaded
                        ? "Downloaded ✓"
                        : "Download blurred video ↓"}
                  </button>
                  <button
                    className="plate-secondary"
                    onClick={reset}
                    disabled={downloading}
                  >
                    Process another video
                  </button>
                </div>
              )}
            </section>
          )}
          <p className="plate-retention">
            Download promptly: processed videos are removed after download or 10
            minutes after processing completes.
          </p>
        </section>
        <aside className="plate-aside">
          <section className="plate-example">
            <p className="ed-eyebrow">A small change. A private detail.</p>
            <div className="plate-comparison">
              <div>
                <span>Before</span>
                <div className="plate-license">AB 123 CD</div>
              </div>
              <span aria-hidden="true">↓</span>
              <div>
                <span>After</span>
                <div className="plate-license plate-license-blurred">
                  <span>AB 123 CD</span>
                </div>
              </div>
            </div>
            <p>
              Illustration of plate blurring.
              <br />
              Your video’s results may vary.
            </p>
          </section>
          <section className="plate-how">
            <p className="ed-eyebrow">How it works</p>
            <ol>
              <li>
                <span>01</span>
                <div>
                  <h3>Choose your video</h3>
                  <p>MP4, MOV, or MKV. H.264 is recommended.</p>
                </div>
              </li>
              <li>
                <span>02</span>
                <div>
                  <h3>Let us handle the plates</h3>
                  <p>Detection and blurring run in the background.</p>
                </div>
              </li>
              <li>
                <span>03</span>
                <div>
                  <h3>Download & review</h3>
                  <p>
                    Save the processed video, then check the result before
                    sharing.
                  </p>
                </div>
              </li>
            </ol>
          </section>
        </aside>
      </div>
      <section className="plate-tips">
        <p className="ed-eyebrow">For a better result</p>
        <div>
          <article>
            <h2>Keep it clear.</h2>
            <p>
              720p or 1080p footage with visible plates gives detection more
              detail to work with.
            </p>
          </article>
          <article>
            <h2>Keep it steady.</h2>
            <p>
              Clear frames and steady camera movement help the tool find number
              plates.
            </p>
          </article>
          <article>
            <h2>Keep it focused.</h2>
            <p>
              Trim unnecessary footage before uploading. Larger videos take
              longer to process.
            </p>
          </article>
        </div>
      </section>
      <Link className="plate-back" to="/smart-tools">
        ← Explore all tools
      </Link>
    </div>
  );
}
